// ViewDetailsModal.tsx - Updated: row-click detail view with action buttons
import React, { useState } from 'react';
import './CompanyManagement.css';
import './ViewDetailsModal.css';
import { Organization } from './types';

interface ViewDetailsModalProps {
  organization: Organization;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (organization: Organization) => void;
  onManageDivisions?: (organization: Organization) => void;
  onManageUsers?: (organization: Organization) => void;
  onDelete?: (organization: Organization) => void;
}

export const ViewDetailsModal: React.FC<ViewDetailsModalProps> = ({
  organization,
  isOpen,
  onClose,
  onEdit,
  onManageDivisions,
  onManageUsers,
  onDelete
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!isOpen) return null;

  // Derive authorization summary
  const authorizedCount = organization.regions?.filter(r => r.authorized).length || 0;
  const totalRegions = organization.regions?.length || 0;
  const allAuthorized = totalRegions > 0 && authorizedCount === totalRegions;
  const noneAuthorized = authorizedCount === 0;

  const handleAction = (action: ((org: Organization) => void) | undefined) => {
    if (action) {
      onClose();
      action(organization);
    }
  };

  return (
    <div className="modal-overlay view-details-overlay" onClick={onClose}>
      <div className="view-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="view-details-header">
          <div className="view-details-header-left">
            <h2 className="view-details-title">{organization.companyName}</h2>
            {organization.blacklisted ? (
              <span className="vdm-status-badge blacklisted">Blacklisted</span>
            ) : (
              <span className="vdm-status-badge active">Active</span>
            )}
          </div>
          <button 
            type="button" 
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="view-details-body">
          <div className="view-details-section">
            <h3 className="section-title">Basic Info</h3>
          </div>

          <div className="view-details-grid">
            <div className="view-details-left">
              <div className="detail-field">
                <label className="detail-label">Company Name</label>
                <div className="detail-value company-name">{organization.companyName}</div>
              </div>

              <div className="detail-field">
                <label className="detail-label">Phone</label>
                <div className="detail-value phone">{organization.contactNumber || 'N/A'}</div>
              </div>

              <div className="detail-field">
                <label className="detail-label">Company URL</label>
                <div className="detail-value company-url">
                  {organization.url ? (
                    <a href={organization.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                      {organization.url}
                    </a>
                  ) : (
                    'N/A'
                  )}
                </div>
              </div>

              <div className="detail-field">
                <label className="detail-label">Email</label>
                <div className="detail-value email">
                  <a href={`mailto:${organization.contactEmail}`} onClick={(e) => e.stopPropagation()}>
                    {organization.contactEmail}
                  </a>
                </div>
              </div>

              <div className="detail-field">
                <label className="detail-label">Authorization Summary</label>
                <div className="detail-value authorization">
                  {totalRegions === 0 
                    ? 'No Regions Assigned'
                    : allAuthorized 
                      ? `Authorized in all ${totalRegions} regions`
                      : noneAuthorized
                        ? 'Not authorized in any region'
                        : `Authorized in ${authorizedCount} of ${totalRegions} regions`
                  }
                </div>
              </div>
            </div>

            <div className="view-details-right">
              {/* Regions Section */}
              <div className="detail-field">
                <label className="detail-label">Regions ({totalRegions})</label>
                <div className="detail-value regions">
                  {organization.regions && organization.regions.length > 0 ? (
                    <div className="regions-list">
                      {organization.regions.map((region, index) => (
                        <div key={`${region.regionId}-${index}`} className="region-item-display">
                          <div className="region-main-info">
                            <span className="region-name">{region.regionName}</span>
                            <span className="region-code">({region.regionCode})</span>
                          </div>
                          
                          <div className="region-meta-info">
                            <span className={`region-auth-badge ${region.authorized ? 'authorized' : 'not-authorized'}`}>
                              {region.authorized ? 'Authorized' : 'Not Authorized'}
                            </span>

                            <span className={`region-status ${region.isActive ? 'active' : 'inactive'}`}>
                              {region.isActive ? 'Active' : 'Inactive'}
                            </span>
                            
                            {region.fileId && (
                              <span className="region-file-id">
                                File: {region.fileId}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    'No regions assigned'
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Description Field - Full Width */}
          <div className="view-details-section">
            <div className="detail-field full-width">
              <label className="detail-label">Description</label>
              <div className="detail-value description">
                {organization.description || 'No description provided'}
              </div>
            </div>
          </div>
        </div>

        {/* Footer with action buttons */}
        <div className="view-details-footer">
          <div className="vdm-actions-left">
            {onDelete && !showDeleteConfirm && (
              <button 
                type="button" 
                className="vdm-action-btn delete"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
                Delete
              </button>
            )}
            {showDeleteConfirm && (
              <div className="vdm-delete-confirm">
                <span className="vdm-delete-confirm-text">Delete this company?</span>
                <button 
                  type="button" 
                  className="vdm-action-btn delete-confirm"
                  onClick={() => handleAction(onDelete)}
                >
                  Yes, Delete
                </button>
                <button 
                  type="button" 
                  className="vdm-action-btn cancel-delete"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          <div className="vdm-actions-right">
            {onManageDivisions && (
              <button 
                type="button" 
                className="vdm-action-btn secondary"
                onClick={() => handleAction(onManageDivisions)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
                </svg>
                Divisions
              </button>
            )}
            {onManageUsers && (
              <button 
                type="button" 
                className="vdm-action-btn secondary"
                onClick={() => handleAction(onManageUsers)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                Users
              </button>
            )}
            {onEdit && (
              <button 
                type="button" 
                className="vdm-action-btn primary"
                onClick={() => handleAction(onEdit)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                Edit
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};